/**
 * v3.21-1716 · Authenticated PDF Viewer
 *
 * Fetches a PDF from a bearer-authenticated Hub API endpoint,
 * converts to blob → object URL, and renders inline via <iframe>.
 * The browser NEVER navigates directly to a bearer-auth endpoint.
 *
 * Used by:
 *   • Deal Workspace → Service Authorization card
 *   • Deal Workspace → Closing Packet card
 *   • Ghost success screen → Preview Service Authorization
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Download, X, RefreshCw, AlertTriangle } from 'lucide-react';
import api from '../lib/api';

export function AuthenticatedPdfViewer({
  open,
  onOpenChange,
  title,
  description,
  fetchPath,          // API path e.g. /clients/x/deals/y/service-auth/preview.pdf
  filename,           // fallback filename for download button
}) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  const fetchPdf = useCallback(async () => {
    if (!fetchPath) return;
    setLoading(true); setError(null);
    // Revoke previous object URL to free memory.
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    try {
      const res = await api.get(fetchPath, { responseType: 'blob' });
      // Guard: some FastAPI JSON error responses arrive as application/json.
      if (res.headers?.['content-type']?.includes('application/json')) {
        const text = await res.data.text();
        throw new Error(text || 'PDF request returned JSON error');
      }
      const url = URL.createObjectURL(res.data);
      setObjectUrl(url);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load PDF');
      setObjectUrl(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPath]);

  useEffect(() => {
    if (open && fetchPath) fetchPdf();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchPath]);

  const handleDownload = () => {
    if (!objectUrl) return;
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'document.pdf';
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] h-[92vh] flex flex-col p-0 gap-0"
        data-testid="auth-pdf-viewer"
      >
        <DialogHeader className="px-4 py-3 border-b flex flex-row items-center justify-between space-y-0">
          <div className="min-w-0">
            <DialogTitle className="text-sm font-semibold truncate">{title || 'Document'}</DialogTitle>
            {description && (
              <DialogDescription className="text-[11px] text-slate-500 truncate">
                {description}
              </DialogDescription>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                    onClick={fetchPdf} disabled={loading}
                    data-testid="auth-pdf-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                    onClick={handleDownload} disabled={!objectUrl}
                    data-testid="auth-pdf-download">
              <Download className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2"
                    onClick={() => onOpenChange(false)}
                    data-testid="auth-pdf-close">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 bg-slate-100 relative overflow-hidden">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
              <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
            </div>
          )}
          {error && !loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-red-600 p-6"
                 data-testid="auth-pdf-error">
              <AlertTriangle className="h-5 w-5" />
              <span className="text-xs font-semibold">Could not load document</span>
              <span className="text-[11px] text-slate-500 text-center max-w-md">
                {typeof error === 'string' ? error : JSON.stringify(error)}
              </span>
              <Button size="sm" variant="outline" className="h-7 text-xs mt-2" onClick={fetchPdf}>
                Retry
              </Button>
            </div>
          )}
          {objectUrl && !error && (
            <iframe
              title={title || 'PDF Preview'}
              src={objectUrl}
              className="w-full h-full border-0"
              data-testid="auth-pdf-iframe"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AuthenticatedPdfViewer;
